import React, { useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import Configs from 'config';
import { get, isEmpty, map } from 'lodash';
import { RootState } from 'modules';
import EditorComponent from 'components/base/EditorComponent';
import NoMatch from 'components/base/error/NoMatch';
import { showConfirm } from 'components/modal/showConfirm';
import { getBoardComments, createBoardComment, deleteBoardComment } from 'libs/api/board';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { usePermission, useTimezone, usePagination, useDataApi } from 'libs/hooks';
import { filteredFileNames, showNotification } from 'libs/utils';
import { TBoardDetail, TCreateBoardComment, TUpdateBoardDetail, TBoardComment } from 'modules/board';
import { TSelectList, TListResponse, TPagination } from 'modules/types';
import { CUploadFileProps, CDefaultEditorHeight, CDateFormat, CWindowWidth } from 'constants/base.const';
import { EOperation, ENotificationType, EBoardType } from 'enums/base.enum';
import { message, Row, Col, Form, Divider, Input, Button, Upload, List, Avatar, Select, Spin } from 'antd';
import {
  UploadOutlined,
  InboxOutlined,
  LinkOutlined,
  CheckOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { device, PrimaryColor, FormWrapper } from 'GlobalStyles';

export const BoardDetailWrapper = styled.div`
  color: #000;
  padding-top: 40px;
  background: #fff;
  max-width: ${CWindowWidth - 500}px;
  margin: auto;
  .ant-divider.no-margin {
    margin: 0px !important;
  }
  .box-title {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    border-top: 1px solid #f0f0f0;
    background-color: #fff;
    padding: 15px 20px;
    margin: 0px !important;
  }
  .box-sub-title {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    padding: 5px 20px;
    .label,
    .value {
      font-size: 12px;
    }
  }

  .title-item {
    margin: 0px;
    word-wrap: break-word;
    word-break: break-all;
  }

  .label {
    font-weight: bold;
    margin-right: 10px;
    font-size: 14px;
  }
  .value {
    font-weight: normal;
    font-size: 14px;
  }

  .body-edit {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    min-height: ${CDefaultEditorHeight}px;
    background-color: #f5f5f5;
    padding: 40px 20px;
    .tox-tinymce {
      z-index: 0;
    }
    margin-bottom: 10px;
  }

  .body-view {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    min-height: ${CDefaultEditorHeight}px;
    padding: 40px 20px;
    overflow-x: hidden;
    .tox-tinymce {
      border: none;
      z-index: 0;
    }
    .tox-editor-header {
      display: none;
    }
    img {
      max-width: 100%;
      height: auto;
    }
  }

  .btn-bottom {
    padding: 20px 0px;
    @media screen and ${device.md} {
      padding: 5px;
    }
  }

  .box-attachments {
    font-weight: bold;
    background-color: #f5f5f5;
    padding: 15px 20px;

    .file-name {
      color: ${PrimaryColor};
      font-weight: normal;
      display: block;
    }
  }
`;

const BoardComment = ({ record }: { record?: TBoardDetail }) => {
  const [pagination, setPagination] = usePagination(10);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromiseComments = getBoardComments.bind(
    null,
    Number(get(record, 'id')),
    get(record, 'board_type') as EBoardType,
    {
      params: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    },
  );
  const [{ data: comments, loading: commentsLoading }, setCallbackComments] = useDataApi<
    TListResponse<TBoardComment>
  >(getPromiseComments, {
    contents: [],
    last: false,
  });
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const [form] = Form.useForm();
  const [getDateTime] = useTimezone();
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  useEffect(() => {
    setCallbackComments(() => getPromiseComments);
  }, [pagination]);

  function onClickCreateComment() {
    form
      .validateFields()
      .then((value) => {
        (async (param: TCreateBoardComment) => {
          try {
            setSubmitButtonLoading(true);
            await createBoardComment(Number(get(record, 'id')), {
              ...param,
              board_type: get(record, 'board_type'),
            });

            showNotification(ENotificationType.Success, '댓글이 등록되었습니다.');
            form.resetFields();
            reloadPage({
              current: pagination.current,
              pageSize: pagination.pageSize,
            });
          } catch (e) {
            handleFieldError(e, form);
            throw e;
          } finally {
            setSubmitButtonLoading(false);
          }
        })(value as TCreateBoardComment);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  async function handleDeleteComment(commentId: number) {
    try {
      await deleteBoardComment(commentId);

      showNotification(ENotificationType.Success, '댓글이 삭제되었습니다.');
      reloadPage({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });
    } catch (e) {
      throw e;
    }
  }

  return (
    <div style={{ marginBottom: '60px' }}>
      {get(comments, 'contents', []).length > 0 && (
        <>
          <div style={{ padding: '20px 0px' }}>
            <strong>Comments</strong>: {get(comments, 'total', 0)}
          </div>
          <List
            pagination={{
              ...pagination,
              total: get(comments, 'total', 0),
              position: 'bottom',
              hideOnSinglePage: true,
              onChange: (page) => {
                reloadPage({
                  current: page,
                  pageSize: pagination.pageSize,
                });
              },
            }}
            loading={commentsLoading}
            dataSource={get(comments, 'contents', [])}
            renderItem={(item) => (
              <div>
                <Row align='top' gutter={[8, 0]}>
                  <Col>
                    <Avatar src={get(item, 'created_by.profile_image.absolute_url')} />
                  </Col>
                  <Col>
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#000',
                        lineHeight: '15px',
                      }}
                    >
                      {get(item, 'created_by.fullname')}
                      {get(item, 'created_by.id') === get(currentUser, 'id') && (
                        <div
                          style={{
                            marginLeft: '10px',
                            cursor: 'pointer',
                            display: 'inline-block',
                          }}
                          onClick={() =>
                            showConfirm(
                              () => handleDeleteComment(Number(get(item, 'id'))),
                              '댓글을 삭제하시겠습니까?',
                            )
                          }
                        >
                          <DeleteOutlined />
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#747474' }}>
                      {getDateTime(get(item, 'created_at')).format(CDateFormat)}
                    </div>
                  </Col>
                </Row>
                <div
                  style={{
                    lineHeight: '22px',
                    fontSize: '14px',
                    marginTop: '5px',
                    color: '#000',
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                >
                  {get(item, 'body')}
                </div>
                <Divider style={{ margin: '20px 0px !important' }} />
              </div>
            )}
          />
        </>
      )}
      {currentUser && (
        <div style={{ marginTop: '10px' }}>
          <strong>
            <CheckOutlined />
            &nbsp;댓글 쓰기
          </strong>
          <FormWrapper style={{ paddingTop: '10px' }}>
            <Form form={form} layout='vertical' className='form-normal'>
              <Form.Item>
                <Input.Group>
                  <Row align='top'>
                    <Col flex='auto'>
                      <Form.Item name='body' className='form-item no-margin' rules={[{ required: true }]}>
                        <Input.TextArea
                          className='form_textarea'
                          maxLength={1000}
                          autoSize={{ minRows: 3 }}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex='100px'>
                      <Form.Item shouldUpdate={true}>
                        {() => (
                          <Button
                            size='large'
                            htmlType='submit'
                            loading={submitButtonLoading}
                            onClick={onClickCreateComment}
                          >
                            등록
                          </Button>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Input.Group>
              </Form.Item>
            </Form>
          </FormWrapper>
        </div>
      )}
    </div>
  );
};

type TBoardDetailProps = {
  operation: EOperation;
  pathName: string;
  promiseCreate?: any;
  promiseUpdate?: any;
  promiseDelete?: any;
  getExtraData?: () => any; // 웨비나 같은 곳에서 글 생성시 추가 정보 제공할 때 사용
  record?: TBoardDetail;
  categories?: TSelectList;
  activateComment?: boolean;
  loading?: boolean;
  useFile?: boolean;
  useThumbnail?: boolean;
};

const { Option } = Select;
export const BoardDetail = ({
  operation,
  pathName,
  promiseCreate,
  promiseDelete,
  promiseUpdate,
  record,
  categories,
  getExtraData,
  activateComment = false,
  loading = false,
  useFile = true,
  useThumbnail = true,
}: TBoardDetailProps) => {
  const { adminPermission } = usePermission();
  const [getDateTime] = useTimezone();
  const history = useHistory();

  const BoardTitle = () => {
    return (
      <>
        <Row justify='space-between' align='top' className='box-title' gutter={[3, 3]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <span style={{ fontSize: '16px', color: '#000' }}>{get(record, 'title')}</span>
          </Col>
        </Row>
        <Divider className='no-margin' />
        <Row className='box-sub-title' justify='start'>
          <Col>
            <div className='title-item' style={{ marginRight: '10px' }}>
              <span className='label'>작성일자</span>
              <span className='value'>
                <FormattedDate
                  value={getDateTime(get(record, 'created_at', '')).format(CDateFormat)}
                  year='numeric'
                  month='long'
                  day='2-digit'
                />
              </span>
            </div>
          </Col>
          {get(record, 'hit_count') && (
            <Col>
              <div className='title-item'>
                <span className='label'>조회수</span>
                <span className='value'>{get(record, 'hit_count')}</span>
              </div>
            </Col>
          )}
        </Row>
      </>
    );
  };

  const BoardView = () => {
    const { adminPermission } = usePermission();

    async function onClickDelete() {
      try {
        await promiseDelete(get(record, 'id'));

        history.push({
          pathname: pathName,
          state: {
            notification: {
              type: ENotificationType.Success,
              message: '게시글이 삭제되었습니다.',
            },
          },
        });
      } catch (e) {
        throw e;
      }
    }

    return (
      <div className='no-drag'>
        <Spin tip='로딩중..' spinning={loading}>
          <BoardTitle />
          <div className='body-view'>
            <div dangerouslySetInnerHTML={{ __html: `${get(record, 'body', '')}` }} />
          </div>
          {get(record, 'files', []).length > 0 && (
            <div className='box-attachments'>
              첨부파일
              <br />
              {map(get(record, 'files', []), (v) => (
                <a className='file-name' href={`${Configs.API_HOST}${get(v, 'absolute_url')}`}>
                  <LinkOutlined />
                  &nbsp;{get(v, 'file_name')}
                </a>
              ))}
            </div>
          )}
        </Spin>
        <Divider className='no-margin' />
        <Row justify='space-between' className='btn-bottom'>
          <Col>
            <Link to={pathName}>
              <Button type='primary' size='large'>
                목록
              </Button>
            </Link>
          </Col>
          {adminPermission && (
            <Col>
              <Button
                type='primary'
                size='large'
                onClick={() => showConfirm(() => onClickDelete(), '게시글을 삭제하시겠습니까?')}
                style={{ marginRight: '20px' }}
              >
                삭제
              </Button>
              <Link to={`${pathName}/${EOperation.Edit}/${get(record, 'id')}`}>
                <Button type='primary' size='large'>
                  수정
                </Button>
              </Link>
            </Col>
          )}
        </Row>
        {get(record, 'id') && activateComment === true && (
          <>
            <Divider />
            <BoardComment record={record} />
          </>
        )}
      </div>
    );
  };

  const BoardEdit = ({ isEdit }: { isEdit?: boolean }) => {
    const [form] = Form.useForm();
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

    useEffect(() => {
      if (isEmpty(record) === false) {
        form.setFieldsValue({
          title: get(record, 'title'),
          category: get(record, 'category'),
          files: map(get(record, 'files'), (v) => ({
            uid: v.id,
            status: 'done',
            name: v.file_name,
          })),

          images:
            get(record, 'thumbnail.id', 0) > 0
              ? [
                  {
                    uid: get(record, 'thumbnail.id'),
                    status: 'done',
                    name: 'thumbnail',
                  },
                ]
              : [],
          body: get(record, 'body'),
        });
      }
    }, [record]);

    function onClickSubmit() {
      form
        .validateFields()
        .then((value) => {
          handleUpdate(value as TUpdateBoardDetail);
        })
        .catch((errorInfo) => {
          moveToFirstError(form, errorInfo.errorFields);
        });
      async function handleUpdate(value: TUpdateBoardDetail) {
        try {
          setSubmitButtonDisabled(true);
          const extraData = getExtraData ? getExtraData() : {};
          const data = {
            title: get(value, 'title'),
            body: get(value, 'body'),
            category: get(value, 'category'),
            file_ids: filteredFileNames(get(value, 'files')),
            thumbnail_ids: filteredFileNames(get(value, 'images')),
            ...extraData,
          };
          isEdit === true ? await promiseUpdate(get(record, 'id'), data) : await promiseCreate(data);

          history.push({
            pathname: pathName,
            state: {
              notification: {
                type: ENotificationType.Success,
                message: isEdit === true ? '게시글이 수정되었습니다.' : '게시글이 생성되었습니다.',
              },
            },
          });
        } catch (e) {
          handleFieldError(e, form);
          throw e;
        } finally {
          setSubmitButtonDisabled(false);
        }
      }
    }

    return (
      <>
        <Form form={form} className='body-edit'>
          <Form.Item noStyle>
            <Input.Group>
              <Row>
                {categories && (
                  <Col flex='120px'>
                    <Form.Item name='category'>
                      <Select size='large' style={{ width: '100%' }} placeholder='카테고리'>
                        {categories.map((v) => {
                          return (
                            <Option value={v.value} key={v.value}>
                              {v.label}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col flex='auto'>
                  <Form.Item name='title' rules={[{ required: true, message: '제목을 입력해주세요' }]}>
                    <Input size='large' placeholder='제목' />
                  </Form.Item>
                </Col>
              </Row>
            </Input.Group>
          </Form.Item>
          <Form.Item name='body' rules={[{ required: true, message: '내용을 입력해주세요' }]}>
            <EditorComponent content={get(record, 'body', '')} />
          </Form.Item>
          {useFile === true && (
            <Form.Item
              noStyle
              name='files'
              shouldUpdate={(prevValues, currentValues) => prevValues.files !== currentValues.files}
              valuePropName='fileList'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload.Dragger
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/board/file`}
                multiple={true}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;
                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  }
                }}
              >
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>파일 업로드하기</p>
                <p className='ant-upload-hint'>파일을 드래그하거나 여기를 클릭해주세요</p>
              </Upload.Dragger>
            </Form.Item>
          )}
          {useThumbnail === true && (
            <Form.Item
              noStyle
              name='images'
              shouldUpdate={(prevValues, currentValues) => prevValues.images !== currentValues.images}
              valuePropName='fileList'
              getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            >
              <Upload
                name='file'
                {...CUploadFileProps}
                action={`${Configs.API_HOST}/board/image`}
                onChange={(info: UploadChangeParam) => {
                  const { status } = info.file;
                  if (status === 'done') {
                    message.success(`${info.file.name} 업로드에 성공했습니다.`);
                  } else if (status === 'error') {
                    message.error(`${info.file.name} 업로드에 실패했습니다.`);
                  }
                }}
              >
                <Button icon={<UploadOutlined />}>썸네일용 이미지 업로드</Button>
              </Upload>
            </Form.Item>
          )}
        </Form>
        <Divider />
        <Row justify='space-between' className='btn-bottom'>
          <Col>
            <Link to={pathName}>
              <Button type='primary' size='large'>
                목록
              </Button>
            </Link>
          </Col>
          <Col>
            <Button type='primary' size='large' loading={submitButtonDisabled} onClick={onClickSubmit}>
              등록
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <BoardDetailWrapper>
      {(operation === EOperation.View && (
        //콘텐츠 단순 보기
        <BoardView />
      )) ||
        (operation === EOperation.Edit && (
          // 콘텐츠 수정하기(어드민 계정만)
          <>{adminPermission && <BoardEdit isEdit={true} />}</>
        )) ||
        //콘텐츠 글쓰기 (어드민 계정만)
        (operation === EOperation.Create && <>{adminPermission && <BoardEdit />}</>) || <NoMatch />}
    </BoardDetailWrapper>
  );
};

export default BoardDetail;
